import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Toolbar, ToolbarHeading, ToolbarPageTitle } from '@/partials/toolbar';
import { Container, BackButton, Button, Shimmer } from '@/components';
import {
  useGetServiceProviderBookingByIdQuery,
  useApproveServiceBookingMutation,
  useRejectServiceBookingMutation,
  useUpdateServiceProviderBookingStatusMutation,
  useUpdateServiceProviderBookingMutation,
} from '@/services/ServiceProviderBookingService';
import { BookingStatus } from '@/types/booking-management.types';
import { formatIsoDate } from '@/utils/Date';
import { handleError } from '@/utils/BaseQuery';
import { formatFinnishCurrency, formatFinnishNumber, formatFinnishCoordinate } from '@/utils';
import { ConfirmationDialog } from '@/components/dialogBox';
import { usePermissions } from '@/hooks/usePermissions';
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';

interface EditFormData {
  start_date: string;
}

const bookingValidationSchema = Yup.object().shape({
  start_date: Yup.string().required('Start date is required'),
});

const ServiceBookingDetails = () => {
  const { bookingId: id } = useParams<{ bookingId: string }>();
  const location = useLocation();
  const isEditMode = location.pathname.includes('/edit');
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const { data, isLoading, isError, refetch } =
    useGetServiceProviderBookingByIdQuery(id as string, {
      skip: !id,
    });

  const bookingDetails = data;

  const [approveBooking, { isLoading: isApproving }] =
    useApproveServiceBookingMutation();
  const [rejectBooking, { isLoading: isRejecting }] =
    useRejectServiceBookingMutation();
  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateServiceProviderBookingStatusMutation();
  const [updateBooking, { isLoading: isUpdatingBooking }] =
    useUpdateServiceProviderBookingMutation();

  const isUpdating = isApproving || isRejecting || isUpdatingStatus;

  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    bookingId: string | null;
    action: 'approve' | 'reject' | 'cancel' | null;
    reason?: string;
  }>({ isOpen: false, bookingId: null, action: null });

  const [editFormData, setEditFormData] = useState<EditFormData>({
    start_date: '',
  });
  const [editErrors, setEditErrors] = useState<{ [key: string]: string }>({});
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (bookingDetails) {
      setEditFormData({
        start_date: bookingDetails.start_date.split('T')[0],
      });
    }
  }, [bookingDetails]);

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const validateField = async (name: string, value: string) => {
    try {
      await bookingValidationSchema.validateAt(name, {
        ...editFormData,
        [name]: value,
      });
      setEditErrors((prev) => ({ ...prev, [name]: '' }));
    } catch (err) {
      const error = err as Yup.ValidationError;
      setEditErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const handleSaveChanges = async () => {
    try {
      await bookingValidationSchema.validate(editFormData, {
        abortEarly: false,
      });
      const updateData = {
        start_date: editFormData.start_date,
        end_date: editFormData.start_date,
        status: BookingStatus.PENDING_CONFIRMATION,
      };
      const response = await updateBooking({
        id: id as string,
        update: updateData,
      }).unwrap();
      setIsEditing(false);
      refetch();

      enqueueSnackbar(response.message || 'Booking updated successfully', {
        variant: 'success',
      });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setEditErrors(validationErrors);
        enqueueSnackbar(
          `Please fix the form errors (${err.inner.length} errors occurred)`,
          { variant: 'error' }
        );
      } else {
        handleError(err as Error);
      }
    }
  };

  const handleActionClick = (
    bookingId: string,
    action: 'approve' | 'reject' | 'cancel'
  ) => {
    setConfirmActionState({ isOpen: true, bookingId, action });
  };

  const handleConfirmAction = async () => {
    const { bookingId, action, reason } = confirmActionState;
    if (!bookingId || !action) return;

    try {
      if (action === 'approve') {
        await approveBooking(bookingId).unwrap();
        enqueueSnackbar('Booking approved successfully.', {
          variant: 'success',
        });
        refetch();
      } else if (action === 'reject') {
        await rejectBooking(bookingId).unwrap();
        enqueueSnackbar('Booking rejected successfully.', {
          variant: 'success',
        });
        refetch();
      } else if (action === 'cancel') {
        await updateStatus({
          id: bookingId,
          update: {
            status: BookingStatus.CANCELLED,
            cancellation_reason: reason,
          },
        }).unwrap();
        enqueueSnackbar('Booking cancelled successfully.', {
          variant: 'success',
        });
        refetch();
      } else navigate(-1);

      setConfirmActionState({
        isOpen: false,
        bookingId: null,
        action: null,
        reason: undefined,
      });
    } catch (error) {
      handleError(error as Error);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmActionState((prev) => ({ ...prev, reason: e.target.value }));
  };

  const getStatusDisplay = (status: number) => {
    let textColorClass = '';
    let statusText = '';
    switch (status) {
      case BookingStatus.PENDING:
        textColorClass = 'text-yellow-700 dark:text-yellow-600 contrast:text-yellow-600';
        statusText = 'Pending';
        break;
      case BookingStatus.CONFIRMED:
        textColorClass = 'text-green-700 dark:text-green-600 contrast:text-green-600';
        statusText = 'Confirmed';
        break;
      case BookingStatus.CANCELLED:
        textColorClass = 'text-red-600 dark:text-red-400 contrast:text-red-300';
        statusText = 'Cancelled';
        break;
      case BookingStatus.COMPLETED:
        textColorClass = 'text-blue-600 dark:text-blue-400 contrast:text-blue-400';
        statusText = 'Completed';
        break;
      case BookingStatus.PENDING_CONFIRMATION:
        textColorClass = 'text-gray-600 dark:text-gray-700';
        statusText = 'Pending Confirmation';
        break;
      case BookingStatus.REJECTED:
        textColorClass = 'text-red-600 dark:text-red-400 contrast:text-red-300';
        statusText = 'Rejected';
        break;
      default:
        textColorClass = 'text-gray-800 dark:text-gray-800';
        statusText = 'Pending';
    }
    return (
      <span className={`font-normal ${textColorClass}`}>{statusText}</span>
    );
  };

  const getPaymentTypeDisplay = (type: number) => {
    switch (type) {
      case 1:
        return 'Credit Card';
      case 2:
        return 'Bank Transfer';
      default:
        return 'Other';
    }
  };

  return (
    <Container>
      {/* Toolbar with action buttons */}
      <Toolbar>
        <ToolbarHeading>
          <div className="flex gap-2 items-center">
            <BackButton />
            <ToolbarPageTitle text="Service Booking Details" />
          </div>
        </ToolbarHeading>
        <div className="flex gap-2.5 justify-center items-center mt-4 sm:mt-0">
          {bookingDetails?.status === BookingStatus.PENDING_CONFIRMATION && (
            <div className="flex gap-2.5 justify-center items-center mt-4 sm:mt-0">
              {hasPermission('service_management_update') && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() =>
                    handleActionClick(bookingDetails.id, 'approve')
                  }
                  disabled={isUpdating}
                >
                  Approve
                </Button>
              )}
              {hasPermission('service_management_update') && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => handleActionClick(bookingDetails.id, 'reject')}
                  disabled={isUpdating}
                >
                  Reject
                </Button>
              )}
            </div>
          )}
          {bookingDetails &&
            bookingDetails?.status !== BookingStatus.PENDING_CONFIRMATION &&
            bookingDetails?.status !== BookingStatus.CANCELLED &&
            bookingDetails.status !== BookingStatus.REJECTED &&
            bookingDetails?.status !== BookingStatus.COMPLETED && (
              <div className="flex gap-2.5 justify-center items-center mt-4 sm:mt-0">
                {hasPermission('service_management_update') && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() =>
                      handleActionClick(bookingDetails.id, 'cancel')
                    }
                    disabled={isUpdating}
                  >
                    Cancel Booking
                  </Button>
                )}
              </div>
            )}
        </div>
      </Toolbar>

      {/* Booking Details Section */}
      <div className="card mx-auto mb-[30px]">
        {isLoading ? (
          <Shimmer height="174px" borderRadius="8px" />
        ) : isError ? (
          <div className="p-5 text-red-600" role="alert">
            Error loading data. Please try again.
          </div>
        ) : (
          <section className="p-5 grid gap-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7.5 items-stretch py-4 px-3">
              <div className="flex flex-col items-start gap-2">
                <span className="font-semibold text-gray-900">ID:</span>
                <span className="font-normal text-gray-700 break-all md:break-normal">
                  {bookingDetails?.id}
                </span>
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">Date:</span>
                {isEditing ? (
                  <div className="w-full">
                    <input
                      type="date"
                      name="start_date"
                      min={new Date().toISOString().split('T')[0]}
                      value={editFormData.start_date}
                      onChange={handleEditChange}
                      disabled={isUpdatingBooking}
                      className={`input input-bordered w-full h-12 rounded-lg bg-gray-50 border-2 transition-all duration-200 ${
                        editErrors.start_date ? 'border-red-500' : ''
                      } focus:ring-blue-500 focus:outline-none`}
                      aria-invalid={!!editErrors.start_date}
                      aria-describedby={
                        editErrors.start_date ? 'start_date-error' : undefined
                      }
                      onKeyDown={(e) => {
                        if (e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                    />
                    {editErrors.start_date && (
                      <p
                        id="start_date-error"
                        className="text-red-500 text-xs mt-2"
                        role="alert"
                      >
                        {editErrors.start_date}
                      </p>
                    )}
                  </div>
                ) : (
                  <span className="font-normal text-gray-700">
                    {bookingDetails &&
                      formatIsoDate(bookingDetails.start_date.split('T')[0])}
                  </span>
                )}
              </div>
              {bookingDetails?.start_time && (
                <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                  <span className="font-semibold text-gray-900">
                    Start Time:
                  </span>
                  <span className="font-normal text-gray-700">
                    {bookingDetails.start_time}
                  </span>
                </div>
              )}
              {bookingDetails?.end_time && (
                <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                  <span className="font-semibold text-gray-900">End Time:</span>
                  <span className="font-normal text-gray-700">
                    {bookingDetails.end_time}
                  </span>
                </div>
              )}
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">
                  Total Price:
                </span>
                <span className="font-normal text-gray-700">
                  {bookingDetails &&
                  bookingDetails.total_price !== null &&
                  bookingDetails.total_price !== undefined
                    ? formatFinnishCurrency(bookingDetails.total_price)
                    : '-'}
                </span>
              </div>
              {bookingDetails?.quantity && (
                <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                  <span className="font-semibold text-gray-900">Quantity:</span>
                  <span className="font-normal text-gray-700">
                    {formatFinnishNumber(bookingDetails.quantity, {
                      maximumFractionDigits: 0,
                    })}
                  </span>
                </div>
              )}
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">
                  Payment Type:
                </span>
                <span className="font-normal text-gray-700">
                  {bookingDetails &&
                    getPaymentTypeDisplay(bookingDetails.payment_type)}
                </span>
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">Status:</span>
                {bookingDetails && getStatusDisplay(bookingDetails.status)}
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">
                  Special Requests:
                </span>
                <span className="font-normal text-gray-700">
                  {bookingDetails?.special_requests || '-'}
                </span>
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">
                  Cancellation Reason:
                </span>
                <span className="font-normal text-gray-700">
                  {bookingDetails?.cancellation_reason || '-'}
                </span>
              </div>
              <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                <span className="font-semibold text-gray-900">User:</span>
                <span className="font-normal text-gray-700 break-all">
                  {bookingDetails?.user.name ||
                    bookingDetails?.user.email ||
                    '-'}
                </span>
              </div>
              {bookingDetails?.service && (
                <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                  <span className="font-semibold text-gray-900">Service:</span>
                  <span className="font-normal text-gray-700">
                    {bookingDetails.service.name || '-'}
                  </span>
                </div>
              )}
              {isEditMode &&
                (bookingDetails?.status ===
                  BookingStatus.PENDING_CONFIRMATION ||
                  bookingDetails?.status === BookingStatus.PENDING) && (
                  <div className="flex gap-2.5 justify-center items-center mt-4 sm:mt-0">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setIsEditing(!isEditing)}
                      disabled={isUpdatingStatus || isUpdatingBooking}
                    >
                      {isEditing ? 'Cancel Edit' : 'Edit Date'}
                    </Button>
                    {isEditing && (
                      <Button
                        type="button"
                        variant="primary"
                        onClick={handleSaveChanges}
                        disabled={isUpdatingStatus || isUpdatingBooking}
                      >
                        Save Changes
                      </Button>
                    )}
                  </div>
                )}
            </div>
          </section>
        )}
      </div>

      {/* Service Details Section */}
      {bookingDetails?.service && (
        <>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="Service Details" />
            </ToolbarHeading>
          </Toolbar>
          <div className="card mx-auto mb-[30px]">
            {isLoading ? (
              <Shimmer height="174px" borderRadius="8px" />
            ) : (
              <section className="p-5 grid gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7.5 items-stretch py-4 px-3">
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Service Name:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.service.name || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Description:
                    </span>
                    <span className="font-normal text-gray-700 whitespace-pre-wrap">
                      {bookingDetails.service.description || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Category:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.service.category || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Sub Category:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.service.sub_category || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Service Type:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.service.service_type || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Price Amount:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.service.price_amount !== null &&
                      bookingDetails.service.price_amount !== undefined
                        ? formatFinnishCurrency(
                            bookingDetails.service.price_amount
                          )
                        : '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                    <span className="font-semibold text-gray-900">
                      Booking Slot Type:
                    </span>
                    <span className="font-normal text-gray-700">
                      {bookingDetails.service.booking_slot_type || '-'}
                    </span>
                  </div>
                  {bookingDetails.service.latitude != null &&
                    bookingDetails.service.longitude != null && (
                      <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                        <span className="font-semibold text-gray-900">
                          Coordinates:
                        </span>
                        <span className="font-normal text-gray-700">
                          {formatFinnishCoordinate(
                            Number(bookingDetails.service.latitude)
                          )}
                          ,{' '}
                          {formatFinnishCoordinate(
                            Number(bookingDetails.service.longitude)
                          )}
                        </span>
                      </div>
                    )}
                  {bookingDetails.service.distance != null && (
                    <div className="flex flex-col items-start gap-2 mt-3 sm:mt-0">
                      <span className="font-semibold text-gray-900">
                        Distance from Harbour:
                      </span>
                      <span className="font-normal text-gray-700">
                        {bookingDetails.service.distance}{' '}
                        {bookingDetails.service.distance_unit || ''}
                      </span>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </>
      )}

      <ConfirmationDialog
        open={confirmActionState.isOpen}
        onClose={() =>
          setConfirmActionState({
            isOpen: false,
            bookingId: null,
            action: null,
            reason: undefined,
          })
        }
        onConfirm={handleConfirmAction}
        message={
          confirmActionState.action === 'cancel' ||
          confirmActionState.action === 'reject' ? (
            <>
              Are you sure you want to {confirmActionState.action} this booking?
              <input
                type="text"
                name="booking_action_reason"
                aria-label="Action reason"
                placeholder="Reason (optional)"
                value={confirmActionState.reason || ''}
                onChange={handleReasonChange}
                className="mt-2 w-full p-2 border rounded bg-gray-200 placeholder:text-gray-400"
                disabled={isUpdating}
                autoComplete="off"
              />
            </>
          ) : (
            `Are you sure you want to approve this booking?`
          )
        }
        cancelButtonText="No"
        confirmButtonText="Yes"
        aria-labelledby="action-confirmation-title"
        aria-describedby="action-confirmation-description"
      />
    </Container>
  );
};

export default ServiceBookingDetails;
